/**
 * useAIResolve — one-click AI repair directly from the Compile Check Report.
 *
 * Orchestrates per-issue resolution without navigating to Fix & Continue:
 *   1. Fetch Java file content from S3
 *   2. Build repair prompt (strategy auto-detected by error type)
 *   3. Call Claude via customPromptAI
 *   4. Parse JSON patch from response
 *   5. Apply patch in memory (line replacement)
 *   6. Save patched content as S3 overlay via PUT /editor/file
 *   7. Trigger compile/structural re-run; poll until complete
 *   8. Call onRerunComplete(newJobId) → GateReportView refreshes issue list
 *
 * Hook signature:
 *   useAIResolve(analysisContext, apiBase, gateType, onRerunComplete)
 *   → { resolveState, resolveIssue, resolveAll, closeModal }
 */

import { useState, useCallback, useRef } from 'react'
import { customPrompt as customPromptAI } from '../services/aiService'
import { extractAIPatch } from '../utils/aiJsonParser'

// Maximum number of AI Resolve reruns per session before requiring manual intervention.
// Prevents infinite patch→rerun→patch loops when AI can't converge on a fix.
const MAX_RERUNS = 10

// ── Parse-blocking detection ─────────────────────────────────────────────────
// These patterns match javac errors that BLOCK further parsing. When they are
// fixed, javac can see past the error site and may report new (deeper) errors
// that were always there. An increase in error count after fixing parse-blockers
// is expected compiler behaviour, NOT a regression.
const PARSE_BLOCKING_PATTERNS = [
  /'\)' expected/i,
  /';' expected/i,
  /'\(' expected/i,
  /'\{' expected/i,
  /'\}' expected/i,
  /not a statement/i,
  /illegal start of (expression|type)/i,
  /reached end of file while parsing/i,
  /unclosed string literal/i,
  /class, interface, enum, or record expected/i,
]

function isParseBlocking(msg) {
  if (!msg) return false
  return PARSE_BLOCKING_PATTERNS.some(rx => rx.test(msg))
}

// ── Strategy detection ────────────────────────────────────────────────────────

function detectStrategy(issue, gateType) {
  if (gateType === 'structural') return 'file-structure'
  const msg = (issue.summary || '').toLowerCase()

  // "class, interface, enum, or record expected" = orphaned statement at a specific line.
  // repair-region is more surgical — targets ±30 lines around the error and wraps/removes
  // the stray code. file-structure (whole-file rewrite) tends to miss this pattern.
  if (/class, interface, enum, or record expected/.test(issue.summary || ''))
    return 'repair-region'

  // Paren/semicolon errors are always localized to a single line — surgical fix only.
  if (/[)';].*expected|not a statement/.test(msg))
    return 'repair-region'

  // Whole-file structural problems: package/import misplaced, missing class declaration,
  // brace imbalance across the file, illegal start of expression at top level.
  if (/\b(package|import|brace|reached end of file|illegal start|public type|static import)\b/.test(msg))
    return 'file-structure'

  return 'repair-method'
}

// ── Prompt builders ───────────────────────────────────────────────────────────

// NOTE: repair-method and repair-region do NOT use JSON for the response.
// Multi-line Java code inside a JSON string value breaks JSON.parse (literal newlines).
// Instead we use a simple two-part format: a LINES: X-Y header + a ```java fence.
// The caller uses extractRegionPatchCode() to parse this — same pattern as file-structure.

function buildRepairMethodPrompt(fileContent, issue) {
  const file    = issue.javaLocation?.file || 'unknown.java'
  const line    = issue.javaLocation?.line || 1
  const summary = issue.summary || ''
  const lines   = fileContent.split('\n')
  const start   = Math.max(1, line - 30)
  const end     = Math.min(lines.length, line + 30)
  const snippet = lines.slice(start - 1, end).join('\n').slice(0, 4000)

  return [
    `Repair the broken Java method in file "${file}".`,
    `The method may have: brace mismatches, illegal statements, or misplaced import/package lines.`,
    `Rewrite it with balanced braces and correct Java syntax. Keep the method signature intact.`,
    ``,
    `CONSTRAINTS:`,
    `- Do NOT modify the package statement, import statements, or class declaration.`,
    `- Only fix the method body shown in the region below.`,
    `- Every { in your corrected code MUST have a matching }.`,
    ``,
    `Return EXACTLY this format — no other text:`,
    `LINES: ${start}-${end}`,
    '```java',
    `<corrected Java code for lines ${start}–${end}>`,
    '```',
    ``,
    `Compile error at line ${line}: ${summary}`,
    `Code region (lines ${start}–${end}):`,
    '```java',
    snippet,
    '```',
  ].join('\n')
}

function buildRepairRegionPrompt(fileContent, issue) {
  const file    = issue.javaLocation?.file || 'unknown.java'
  const line    = issue.javaLocation?.line || 1
  const summary = issue.summary || ''
  const lines   = fileContent.split('\n')
  const start   = Math.max(1, line - 30)
  const end     = Math.min(lines.length, line + 30)
  const snippet = lines.slice(start - 1, end).join('\n').slice(0, 4000)

  // Extra guidance for "class, interface, enum, or record expected" — orphaned code
  const isOrphaned = /class, interface, enum, or record expected/i.test(summary)
  const orphanHint = isOrphaned
    ? [
        `IMPORTANT — "class, interface, enum, or record expected" means statements exist`,
        `OUTSIDE any class body at line ${line}. Fix by choosing ONE of these in priority order:`,
        ``,
        `  PRIORITY 1 — DUPLICATE CHECK (most common cause):`,
        `  Scan the region for code that appears BOTH inside a proper method AND again outside`,
        `  the class closing brace. If the orphaned code at line ${line} is a DUPLICATE of code`,
        `  already present inside a method above it, DELETE the orphaned block entirely.`,
        `  Do NOT wrap it in a new method — just remove it. This is the correct fix.`,
        ``,
        `  PRIORITY 2 — PREMATURE CLOSING BRACE:`,
        `  If a "}" above line ${line} closes the class too early (the code is NOT a duplicate),`,
        `  remove or relocate that premature "}" so the statements fall inside the class body.`,
        ``,
        `  PRIORITY 3 — GENUINELY ORPHANED:`,
        `  Only if neither of the above apply: wrap in a private helper method or comment out.`,
      ].join('\n')
    : ''

  return [
    `Fix the Java compile error in file "${file}" at line ${line}.`,
    `The region of code around the error may have brace imbalance or orphaned statements.`,
    `Repair the region so it compiles correctly. Do not change code beyond what is necessary.`,
    orphanHint,
    `CONSTRAINTS:`,
    `- Do NOT modify the package statement, import statements, or class declaration.`,
    `- Only fix the specific region shown below.`,
    `- Every { in your corrected code MUST have a matching }.`,
    ``,
    `Return EXACTLY this format — no other text:`,
    `LINES: ${start}-${end}`,
    '```java',
    `<corrected Java code for lines ${start}–${end}>`,
    '```',
    ``,
    `Compile error at line ${line}: ${summary}`,
    `Region (lines ${start}–${end}):`,
    '```java',
    snippet,
    '```',
  ].filter(Boolean).join('\n')
}

function buildFileStructurePrompt(fileContent, issue) {
  const file       = issue.javaLocation?.file || 'unknown.java'
  const line       = issue.javaLocation?.line || 1
  const summary    = issue.summary || ''
  const content    = fileContent.slice(0, 8000)
  const truncated  = fileContent.length > 8000

  // NOTE: We do NOT ask for JSON here — embedding a full Java file inside a JSON
  // string value breaks JSON.parse (quotes, backslashes, etc.).
  // Instead we ask Claude to return the corrected Java source directly.
  // The caller (runSingleSteps / resolveAll) handles this via extractFileStructureCode().
  return [
    `Return ONLY the corrected Java source code. No JSON. No explanation. No markdown outside one code fence.`,
    ``,
    `This Java file "${file}" has structural problems: package/import lines may be inside`,
    `method bodies, the class declaration may be missing or malformed, or braces may be unbalanced.`,
    ``,
    `Repair the ENTIRE file structure by:`,
    `1. Moving the package statement to line 1 (if present — keep it EXACTLY as written).`,
    `2. Placing all import statements immediately after the package line (keep them EXACTLY as written).`,
    `3. Ensuring the class declaration follows imports.`,
    `4. Balancing all braces — every { must have a matching }.`,
    `5. Removing any package/import lines that appear inside method bodies.`,
    `6. Do NOT change method logic — only fix file structure.`,
    ``,
    `Return ONLY the corrected Java source, wrapped in a single \`\`\`java ... \`\`\` fence.`,
    ``,
    `Trigger error at line ${line}: ${summary}`,
    truncated ? `(File content truncated to 8 000 chars for context)` : '',
    ``,
    `File content:`,
    '```java',
    content,
    '```',
  ].filter(Boolean).join('\n')
}

// ── Batch file prompt — ONE call per file with ALL errors ──────────────────

/**
 * Build a patch-only prompt: sends ±10 lines around each error (not the full file).
 * AI returns line-level patches (not the whole file), keeping tokens small regardless
 * of file size. The caller applies patches to the full file client-side.
 *
 * @param {string} fileContent  - Complete file source (used to extract snippets)
 * @param {Array}  issues       - All compile errors for this file
 * @param {Array}  projectFiles - Optional list of other class names in the project (manifest)
 * @returns {string} prompt
 */
function buildBatchFilePrompt(fileContent, issues, projectFiles = []) {
  const file = issues[0]?.javaLocation?.file || 'unknown.java'
  const lines = fileContent.split('\n')
  const CONTEXT_LINES = 10

  // Build per-error snippets with ±10 lines of context
  const errorSections = issues.map((issue, i) => {
    const errLine = issue.javaLocation?.line || 1
    const col     = issue.javaLocation?.column || ''
    const msg     = issue.summary || issue.message || ''
    const start   = Math.max(1, errLine - CONTEXT_LINES)
    const end     = Math.min(lines.length, errLine + CONTEXT_LINES)
    const snippet = lines.slice(start - 1, end)
      .map((l, j) => {
        const lineNum = start + j
        const marker  = lineNum === errLine ? '>>>' : '   '
        return `${marker} ${lineNum}: ${l}`
      }).join('\n')

    return [
      `ERROR ${i + 1}: Line ${errLine}${col ? `, Column ${col}` : ''}: ${msg}`,
      `Context (lines ${start}-${end}):`,
      snippet,
    ].join('\n')
  }).join('\n\n')

  // Build project manifest
  const manifestSection = projectFiles.length > 0
    ? [
        ``,
        `PROJECT CONTEXT — These classes exist in the project:`,
        ...projectFiles.map(f => `  - ${f}`),
        `If an error references a class NOT in this list, stub it or comment out the reference.`,
      ].join('\n')
    : ''

  return [
    `You are fixing ${issues.length} compile error(s) in "${file}".`,
    `Return ONLY line-level patches. No explanation. No full file.`,
    ``,
    `RESPONSE FORMAT — return one patch per fix, exactly like this:`,
    `PATCH 1:`,
    `LINE: <line_number>`,
    `OLD: <original line content>`,
    `NEW: <corrected line content>`,
    ``,
    `If a fix requires INSERTING a new line (e.g. a missing closing brace), use:`,
    `PATCH N:`,
    `INSERT_AFTER: <line_number>`,
    `NEW: <line to insert>`,
    ``,
    `If a fix requires DELETING a line, use:`,
    `PATCH N:`,
    `DELETE: <line_number>`,
    ``,
    `RULES:`,
    `1. Fix ALL ${issues.length} errors. One PATCH per fix.`,
    `2. Do NOT rewrite the whole file. Only patch the broken lines.`,
    `3. Keep method signatures and logic intact unless the error requires changing them.`,
    `4. Every { must have a matching }.`,
    `5. For "'{' expected": add the missing opening brace to the method signature line.`,
    `6. For "';' expected": add the missing semicolon.`,
    `7. For "cannot find symbol": add an import or comment out the reference.`,
    manifestSection,
    ``,
    `ERRORS:`,
    ``,
    errorSections,
  ].filter(Boolean).join('\n')
}

/**
 * Parse line-level patches from AI response and apply them to the original file content.
 * Returns the patched file content as a single string.
 *
 * AI returns patches in this format:
 *   PATCH 1:
 *   LINE: 60
 *   OLD: public void doSomething()
 *   NEW: public void doSomething() {
 *
 *   PATCH 2:
 *   INSERT_AFTER: 120
 *   NEW: }
 *
 *   PATCH 3:
 *   DELETE: 85
 *
 * @param {object} result       - { response, code } from callAI
 * @param {string} fileContent  - original file content
 * @param {object} issue        - compile issue (for file name)
 * @returns {object|null}  { patchedContent, patchCount, patches }
 */
function applyAIPatches(result, fileContent, issue) {
  const raw = result?.response || result?.code || ''
  if (!raw) return null

  const lines = fileContent.split('\n')
  const patchRegex = /PATCH\s+\d+:/g
  const parts = raw.split(patchRegex).filter(p => p.trim())

  const patches = []

  for (const part of parts) {
    const trimmed = part.trim()

    // LINE: N / OLD: ... / NEW: ...
    const lineMatch = trimmed.match(/^LINE:\s*(\d+)\s*\nOLD:\s*(.*)\nNEW:\s*(.*)/m)
    if (lineMatch) {
      patches.push({
        type: 'replace',
        line: parseInt(lineMatch[1], 10),
        old: lineMatch[2].trim(),
        new: lineMatch[3].trim(),
      })
      continue
    }

    // INSERT_AFTER: N / NEW: ...
    const insertMatch = trimmed.match(/^INSERT_AFTER:\s*(\d+)\s*\nNEW:\s*(.*)/m)
    if (insertMatch) {
      patches.push({
        type: 'insert',
        after: parseInt(insertMatch[1], 10),
        new: insertMatch[2].trim(),
      })
      continue
    }

    // DELETE: N
    const deleteMatch = trimmed.match(/^DELETE:\s*(\d+)/m)
    if (deleteMatch) {
      patches.push({
        type: 'delete',
        line: parseInt(deleteMatch[1], 10),
      })
      continue
    }
  }

  if (patches.length === 0) return null

  // Apply patches in reverse line order so line numbers stay stable
  const sortedPatches = [...patches].sort((a, b) => {
    const lineA = a.line || a.after || 0
    const lineB = b.line || b.after || 0
    return lineB - lineA  // reverse order (bottom to top)
  })

  for (const patch of sortedPatches) {
    if (patch.type === 'replace' && patch.line >= 1 && patch.line <= lines.length) {
      lines[patch.line - 1] = patch.new
    } else if (patch.type === 'insert' && patch.after >= 0 && patch.after <= lines.length) {
      lines.splice(patch.after, 0, patch.new)
    } else if (patch.type === 'delete' && patch.line >= 1 && patch.line <= lines.length) {
      lines.splice(patch.line - 1, 1)
    }
  }

  const patchedContent = lines.join('\n')
  const file = issue.javaLocation?.file || 'unknown.java'

  return {
    patchedContent,
    patchCount: patches.length,
    patches,
    // Legacy patch object format for saveFile compatibility
    legacyPatch: {
      action:           'CUSTOM_EDIT',
      file,
      start_line:       1,
      end_line:         fileContent.split('\n').length,
      replacement_text: patchedContent,
      confidence:       0.85,
      explanation:      `AI applied ${patches.length} line-level patch(es)`,
    }
  }
}

/**
 * Fallback: Extract corrected Java source from a file-structure AI response.
 * Used when the AI returns a full file in a code fence instead of patches.
 */
function extractFileStructureCode(result, issue, totalLines) {
  const file = issue.javaLocation?.file || 'unknown.java'
  const raw = result?.response || result?.code || ''
  if (!raw) return null

  let code = raw.trim()

  const fenceMatch = code.match(/```(?:java)?\s*([\s\S]*?)```/)
  if (fenceMatch) {
    code = fenceMatch[1].trim()
  }

  if (!code) return null

  return {
    action:           'CUSTOM_EDIT',
    file,
    start_line:       1,
    end_line:         totalLines,
    replacement_text: code,
    confidence:       0.75,
    explanation:      'File structure repaired by AI (whole-file rewrite)',
  }
}

/**
 * Extract a region patch from an AI response using the LINES: X-Y + code fence format.
 * Used by repair-region and repair-method strategies to avoid JSON encoding failures
 * (multi-line Java code in a JSON string value breaks JSON.parse with literal newlines).
 *
 * Expected format from Claude:
 *   LINES: 174-209
 *   ```java
 *   <corrected code>
 *   ```
 *
 * Falls back to extractAIPatch (JSON) if the format is not found — handles models that
 * return valid JSON for simple single-line fixes.
 *
 * @param {object} result  - { response, code } from callAI
 * @param {object} issue   - compile issue (for file name fallback)
 * @returns {object|null}  patch object { start_line, end_line, replacement_text, ... }
 */
function extractRegionPatchCode(result, issue) {
  const file = issue.javaLocation?.file || 'unknown.java'
  const raw  = result?.response || result?.code || ''
  if (!raw) return null

  const text = raw.trim()

  // Primary: LINES: X-Y on any line, followed by a code fence
  const linesMatch = text.match(/LINES:\s*(\d+)\s*[-–]\s*(\d+)/i)
  const codeMatch  = text.match(/```(?:java)?\s*([\s\S]*?)```/)

  if (linesMatch && codeMatch) {
    const code = codeMatch[1].trim()
    if (!code) return null
    return {
      action:           'CUSTOM_EDIT',
      file,
      start_line:       parseInt(linesMatch[1], 10),
      end_line:         parseInt(linesMatch[2], 10),
      replacement_text: code,
      confidence:       0.8,
      explanation:      'Region repaired by AI',
    }
  }

  // Fallback: JSON extraction (for models/cases that return valid JSON)
  if (import.meta.env.DEV)
    console.log(' [extractRegionPatchCode] LINES format not found — falling back to JSON extraction')
  return extractAIPatch(result)
}

function buildSafePatchPrompt(fileContent, issue) {
  const file    = issue.javaLocation?.file || 'unknown.java'
  const line    = issue.javaLocation?.line || 1
  const summary = issue.summary || ''
  const lines   = fileContent.split('\n')
  const start   = Math.max(1, line - 8)
  const end     = Math.min(lines.length, line + 8)
  const snippet = lines.slice(start - 1, end).join('\n').slice(0, 2000)

  return [
    `You MUST respond with ONLY valid JSON and absolutely no other text.`,
    ``,
    `Fix the Java compile error below.`,
    `Return exactly this JSON structure:`,
    `{`,
    `  "action": "CUSTOM_EDIT",`,
    `  "file": "${file}",`,
    `  "start_line": ${start},`,
    `  "end_line": ${end},`,
    `  "replacement_text": "<corrected Java code for that region>",`,
    `  "confidence": 0.95,`,
    `  "explanation": "<one sentence describing the fix>"`,
    `}`,
    ``,
    `Error: ${summary}`,
    `File: ${file}, Line: ${line}`,
    issue.recommendation ? `Recommendation: ${issue.recommendation}` : '',
    ``,
    `Code context (lines ${start}–${end}):`,
    '```java',
    snippet,
    '```',
  ].filter(Boolean).join('\n')
}

function buildPrompt(strategy, fileContent, issue) {
  switch (strategy) {
    case 'file-structure': return buildFileStructurePrompt(fileContent, issue)
    case 'repair-region':  return buildRepairRegionPrompt(fileContent, issue)
    case 'safe-patch':     return buildSafePatchPrompt(fileContent, issue)
    case 'repair-method':
    default:               return buildRepairMethodPrompt(fileContent, issue)
  }
}

// ── Patch application ─────────────────────────────────────────────────────────

function applyPatch(fileContent, patch) {
  const lines = fileContent.split('\n')
  const before = lines.slice(0, patch.start_line - 1)
  const after  = lines.slice(patch.end_line)
  const replacement = (patch.replacement_text || '').split('\n')
  return [...before, ...replacement, ...after].join('\n')
}

// ── Brace balance validation ───────────────────────────────────────────────────

function validateBraceBalance(content) {
  let depth = 0
  let inLineComment = false
  let inBlockComment = false
  let inString = false

  for (let i = 0; i < content.length; i++) {
    const ch   = content[i]
    const next = content[i + 1] || ''

    if (inLineComment)  { if (ch === '\n') inLineComment = false; continue }
    if (inBlockComment) { if (ch === '*' && next === '/') { inBlockComment = false; i++ }; continue }
    if (inString)       { if (ch === '\\') { i++; continue } if (ch === '"') inString = false; continue }

    if (ch === '/' && next === '/')  { inLineComment = true; continue }
    if (ch === '/' && next === '*')  { inBlockComment = true; i++; continue }
    if (ch === '"')                  { inString = true; continue }

    if      (ch === '{') depth++
    else if (ch === '}') depth--
  }
  return depth === 0
}

function validatePatchContent(patchedContent, originalContent) {
  if (!patchedContent || patchedContent.trim().length === 0)
    return { valid: false, reason: 'patch produced empty file' }
  if (!validateBraceBalance(patchedContent))
    return { valid: false, reason: 'brace imbalance in patched file' }
  // Guard: package declaration must not be altered by the patch
  if (originalContent) {
    const origPkg = originalContent.split('\n').find(l => /^\s*package\s/.test(l))
    const newPkg  = patchedContent.split('\n').find(l => /^\s*package\s/.test(l))
    if (origPkg && origPkg.trimEnd() !== (newPkg || '').trimEnd())
      return { valid: false, reason: 'package declaration was modified — rejected' }
  }
  return { valid: true }
}

// ── Group issues by file ───────────────────────────────────────────────────────

function groupIssuesByFile(issues) {
  const map = new Map()
  for (const issue of issues) {
    const file = issue.javaLocation?.file || '(unknown)'
    if (!map.has(file)) map.set(file, [])
    map.get(file).push(issue)
  }
  return map
}

// ── Auth helper ───────────────────────────────────────────────────────────────

function authHeaders(extra = {}) {
  const token = localStorage.getItem('modernizeit_token')
  return token
    ? { Authorization: `Bearer ${token}`, ...extra }
    : { ...extra }
}

// ── Initial state ─────────────────────────────────────────────────────────────

const INIT = {
  open:         false,
  mode:         'single',   // 'single' | 'batch'
  step:         null,       // 'fetching'|'analyzing'|'applying'|'saving'|'rerunning'|'done'|'error'
  currentIssue: null,
  batchTotal:   0,
  batchDone:    0,
  batchLog:     [],
  errorMessage: null,
  newJobId:     null,
  summary:      null,       // { applied, failed, total } set on batch completion
  beforeErrors: null,       // compile error count before rerun (from status API)
  afterErrors:  null,       // compile error count after rerun
  rolledBack:   [],         // filenames whose overlays were rolled back due to regression
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useAIResolve(analysisContext, apiBase, gateType, onRerunComplete) {
  const [resolveState, setResolveState] = useState(INIT)

  // Abort controller ref so batch can be cancelled in future
  const abortRef = useRef(false)

  // Rerun counter — prevents infinite AI Resolve loops
  const rerunCountRef = useRef(0)

  // ── Context validation ────────────────────────────────────────────────────

  function validateContext() {
    const jobs = analysisContext?.jobs || {}
    const ingestId = jobs.ingest_job_id
    const caId     = jobs.code_analysis_job_id
    if (!ingestId || !caId) {
      return 'Cannot AI Resolve: missing ingest_job_id or code_analysis_job_id in job context. Use Fix & Continue instead.'
    }
    return null
  }

  function getGateJobId() {
    return (
      analysisContext?.jobId ||
      analysisContext?.jobs?.pre_flight_job_id ||
      analysisContext?.jobs?.java_validation_job_id ||
      null
    )
  }

  // ── Fetch file content ────────────────────────────────────────────────────

  async function fetchFileContent(issue) {
    const gateJobId = getGateJobId()
    if (!gateJobId) throw new Error('No gate job ID available to fetch file content.')
    const path = issue.javaLocation?.file
    if (!path)   throw new Error('Issue has no javaLocation.file — cannot fetch content.')

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] fetchFileContent', {
        gateJobId,
        path,
        analysisJobId: analysisContext?.jobId,
        jobsPflt: analysisContext?.jobs?.pre_flight_job_id,
        jobsJval: analysisContext?.jobs?.java_validation_job_id,
      })
    // [DEBUG/TRACE] A) UI — task A.1/A.2
    console.log('[PREFLIGHT_TRACE]', {
      action: 'editor_get', jobIdUsed: gateJobId, gateType, path,
      analysisJobId: analysisContext?.jobId,
      jobsPflt: analysisContext?.jobs?.pre_flight_job_id,
    })

    const url = `${apiBase}/editor/java/file?job_id=${encodeURIComponent(gateJobId)}&path=${encodeURIComponent(path)}`
    const res = await fetch(url, { headers: authHeaders() })
    if (!res.ok) {
      const body = await res.json().catch(() => ({}))
      throw new Error(body.detail || `Failed to fetch file (${res.status})`)
    }
    const data = await res.json()
    return data.content
  }

  // ── Call AI ───────────────────────────────────────────────────────────────

  async function callAI(fileContent, prompt, issue) {
    return customPromptAI(fileContent, prompt, issue.id, issue.javaLocation?.file, {
      gate_type:        gateType,
      issue_summary:    issue.summary,
      scout_account_id: analysisContext?.scout_account_id,
      user_id:          analysisContext?.user_id,
      application_name: analysisContext?.application_name,
    })
  }

  // ── Parse AI patch ────────────────────────────────────────────────────────

  // Shared utility — handles all model response formats (raw JSON, fenced, embedded)
  const parsePatch = (result) => extractAIPatch(result)

  // ── Save file ─────────────────────────────────────────────────────────────

  async function saveFile(issue, content) {
    const jobs = analysisContext?.jobs || {}
    const body = {
      ingest_job_id:        jobs.ingest_job_id,
      code_analysis_job_id: jobs.code_analysis_job_id,
      relative_path:        issue.javaLocation?.file,
      content,
      editor_scope:         'focused',
    }
    // Set the appropriate gate job field — use getGateJobId() which tracks
    // currentJobId via analysisContext.jobId. This ensures workspace writes target
    // the LATEST gate job (after reruns), not the original job.
    const effectiveGateJobId = getGateJobId()
    if (gateType === 'compile' && effectiveGateJobId) {
      body.pre_flight_job_id = effectiveGateJobId
    }
    // For structural gate (java_validation), workspace write targets the ca job — no extra field needed
    if (jobs.service_generation_job_id) body.service_generation_job_id = jobs.service_generation_job_id
    else if (jobs.code_refactor_job_id) body.code_refactor_job_id = jobs.code_refactor_job_id

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] saveFile', {
        gateType,
        relative_path: body.relative_path,
        editor_scope: body.editor_scope,
        pre_flight_job_id: body.pre_flight_job_id,
        effectiveGateJobId,
        jobs_pre_flight_job_id: jobs.pre_flight_job_id,
        service_generation_job_id: body.service_generation_job_id,
        contentLength: content?.length,
      })
    // [DEBUG/TRACE] A) UI — task A.1/A.2
    console.log('[PREFLIGHT_TRACE]', {
      action: 'editor_put', jobIdUsed: effectiveGateJobId, gateType,
      relative_path: body.relative_path, scope: body.editor_scope,
      pre_flight_job_id: body.pre_flight_job_id,
      write_prefix: body.pre_flight_job_id ? 'workspace/' : 'user_edits/',
    })

    // Hard-stop guard: compile gate MUST have pre_flight_job_id or the workspace write
    // will target the wrong S3 prefix and the rerun executor will find 0 files.
    if (gateType === 'compile' && !body.pre_flight_job_id) {
      throw new Error(
        'Cannot save to workspace: no gate job ID available. ' +
        'Close this report and re-open the Compile Check node from the workflow to reload job context.'
      )
    }

    const res = await fetch(`${apiBase}/editor/file`, {
      method: 'PUT',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || `Save failed (${res.status})`)
    }
    if (import.meta.env.DEV) {
      const saveResult = await res.json().catch(() => ({}))
      console.log(' [useAIResolve] saveFile ← success', {
        relative_path: body.relative_path,
        savedUri: saveResult?.uri,
        pre_flight_job_id: body.pre_flight_job_id,
      })
    }
  }

  // ── Trigger rerun ─────────────────────────────────────────────────────────

  async function triggerRerun() {
    // Guard: prevent infinite AI Resolve loops
    rerunCountRef.current += 1
    if (rerunCountRef.current > MAX_RERUNS) {
      throw new Error(
        `AI Resolve has triggered ${MAX_RERUNS} reruns without converging. ` +
        `Use Fix & Continue to manually review and fix the remaining errors.`
      )
    }

    const ctx = analysisContext || {}
    // overlay_job_id tells the executor which pre-flight job's user_edits to consume.
    const overlayJobId = getGateJobId()
    const payload = {
      scout_account_id: ctx.scout_account_id,
      application_name: ctx.application_name,
      user_id:          ctx.user_id,
      overlay_job_id:   overlayJobId,
    }

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] triggerRerun', {
        gateType,
        overlay_job_id: overlayJobId,
        scout_account_id: payload.scout_account_id,
        application_name: payload.application_name,
        analysisJobId: ctx.jobId,
        jobsPflt: ctx.jobs?.pre_flight_job_id,
        jobsJval: ctx.jobs?.java_validation_job_id,
      })
    // [DEBUG/TRACE] A) UI — task A.1/A.2
    console.log('[PREFLIGHT_TRACE]', {
      action: 'ai_resolve_rerun', gateType,
      overlay_job_id: overlayJobId, jobIdUsed: overlayJobId,
      analysisJobId: ctx.jobId,
    })
    const endpoint = gateType === 'compile'
      ? `${apiBase}/pre-flight/start`
      : `${apiBase}/java-validation/start`

    const res = await fetch(endpoint, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || `Rerun start failed (${res.status})`)
    }
    const data = await res.json()
    return data.job_id
  }

  // ── Authoritative error count from compile_result.json (same source as report) ─

  async function fetchCompileResultErrors(jobId) {
    try {
      const url = gateType === 'compile'
        ? `${apiBase}/pre-flight/results/json/${jobId}/compile_result.json`
        : `${apiBase}/java-validation/results/json/${jobId}/java_validation_report.json`
      const r = await fetch(url, { headers: authHeaders() })
      if (!r.ok) return { count: null, errors: [] }
      const data = await r.json()
      // compile_result.json: errors_preview is the full authoritative error list
      if (gateType === 'compile' && Array.isArray(data.errors_preview))
        return { count: data.errors_preview.length, errors: data.errors_preview }
      // java_validation_report.json fallback
      if (Array.isArray(data.findings)) {
        const blocking = data.findings.filter(f => f.severity === 'BLOCK')
        return { count: blocking.length, errors: blocking }
      }
      return { count: null, errors: [] }
    } catch (_) { return { count: null, errors: [] } }
  }

  // ── Poll rerun until complete (returns job ID + final error count) ───────────

  async function pollRerunWithErrors(jobId) {
    const statusBase = gateType === 'compile'
      ? `${apiBase}/pre-flight/status`
      : `${apiBase}/java-validation/status`

    for (let i = 0; i < 270; i++) {  // 9 min max
      await new Promise(r => setTimeout(r, 2000))
      if (abortRef.current) return { finalJobId: jobId, afterErrors: null, afterErrorList: [] }
      try {
        const res = await fetch(`${statusBase}/${jobId}`, { headers: authHeaders() })
        if (!res.ok) continue
        const st   = await res.json()
        const norm = (st.status || '').toLowerCase()
        if (['completed', 'succeeded', 'failed'].includes(norm)) {
          // Use compile_result.json as authoritative source — same as the report UI
          const { count: afterErrors, errors: afterErrorList } = await fetchCompileResultErrors(jobId)
          return { finalJobId: jobId, afterErrors, afterErrorList }
        }
      } catch (_) { /* transient — retry */ }
    }
    return { finalJobId: jobId, afterErrors: null, afterErrorList: [] }  // timed out
  }

  // Legacy wrapper used by single-issue resolveIssue
  async function pollRerun(jobId) {
    const { finalJobId } = await pollRerunWithErrors(jobId)
    return finalJobId
  }

  // ── Single-issue resolve steps 2–6 ───────────────────────────────────────

  async function runSingleSteps(issue, strategy) {
    // Step: fetch
    setResolveState(s => ({ ...s, step: 'fetching' }))
    const fileContent = await fetchFileContent(issue)

    // Step: analyze
    setResolveState(s => ({ ...s, step: 'analyzing' }))
    const effectiveStrategy = strategy === 'auto' ? detectStrategy(issue, gateType) : strategy
    const prompt = buildPrompt(effectiveStrategy, fileContent, issue)
    const result = await callAI(fileContent, prompt, issue)

    // Step: apply (in memory)
    setResolveState(s => ({ ...s, step: 'applying' }))
    if (import.meta.env.DEV) {
      console.log(' [AI_RAW] strategy:', effectiveStrategy)
      console.log(' [AI_RAW] result.response:', JSON.stringify(result?.response?.slice(0, 500)))
      console.log(' [AI_RAW] result.code:', JSON.stringify(result?.code?.slice(0, 500)))
    }

    // JSON is only safe for safe-patch (tiny 8-line region, no multi-line strings).
    // All other strategies return code directly to avoid literal-newline JSON failures.
    const totalLines = fileContent.split('\n').length
    const patch =
      effectiveStrategy === 'file-structure'  ? extractFileStructureCode(result, issue, totalLines) :
      effectiveStrategy === 'repair-region'   ? extractRegionPatchCode(result, issue) :
      effectiveStrategy === 'repair-method'   ? extractRegionPatchCode(result, issue) :
      parsePatch(result)  // safe-patch uses JSON (small region, single-line replacement)
    if (!patch) throw new Error('AI repair failed: could not parse patch from response.')
    const patched = applyPatch(fileContent, patch)

    // Safety gate: reject patch if it creates brace imbalance or modifies package declaration
    const validation = validatePatchContent(patched, fileContent)
    if (!validation.valid)
      throw new Error(`Patch rejected (${validation.reason}). Use Fix & Continue for manual repair.`)

    // Step: save
    setResolveState(s => ({ ...s, step: 'saving' }))
    await saveFile(issue, patched)

    if (import.meta.env.DEV) {
      console.log(` [useAIResolve] Saved overlay for ${issue.javaLocation?.file} (strategy=${effectiveStrategy})`)
    }

    return { patch, patched }
  }

  // ── resolveIssue (exported) ───────────────────────────────────────────────

  const resolveIssue = useCallback(async (issue, strategy = 'auto') => {
    abortRef.current = false

    const contextError = validateContext()
    if (contextError) {
      setResolveState({ ...INIT, open: true, mode: 'single', step: 'error',
        currentIssue: issue, errorMessage: contextError })
      return
    }

    setResolveState({ ...INIT, open: true, mode: 'single', step: 'fetching', currentIssue: issue })

    try {
      await runSingleSteps(issue, strategy)

      // Step: rerun
      setResolveState(s => ({ ...s, step: 'rerunning' }))
      const newJobId = await triggerRerun()
      await pollRerun(newJobId)

      if (import.meta.env.DEV)
        console.log(' [useAIResolve] resolveIssue step=done', { newJobId, gateType, issueId: issue?.id })
      setResolveState(s => ({ ...s, step: 'done', newJobId }))
      onRerunComplete?.(newJobId)

    } catch (err) {
      if (import.meta.env.DEV) console.error(' [useAIResolve] resolveIssue error:', err)
      setResolveState(s => ({ ...s, step: 'error', errorMessage: err.message }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisContext, apiBase, gateType, onRerunComplete])

  // ── resolveAll (exported) — ONE AI call per file with ALL errors ─────────────
  //
  // Patch-only approach: sends ±10 lines of context around each error (not the
  // full file). AI returns line-level patches (LINE/OLD/NEW). Patches are applied
  // client-side. Keeps token count small regardless of file size.

  const resolveAll = useCallback(async (issues, strategy = 'auto') => {
    if (!issues?.length) return
    abortRef.current = false

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] resolveAll start', {
        issueCount: issues?.length, gateType,
        jobId: analysisContext?.jobId, jobs: analysisContext?.jobs,
      })

    const contextError = validateContext()
    if (contextError) {
      setResolveState({ ...INIT, open: true, mode: 'batch', step: 'error',
        errorMessage: contextError, batchTotal: issues.length })
      return
    }

    // Fetch beforeErrors from compile_result.json — authoritative, same source as report
    const gateJobId = getGateJobId()
    let beforeErrors = issues.length  // fallback: count of BLOCK issues passed in
    let beforeErrorList = issues.map(i => i.message || '')  // fallback: use issue messages
    const _fetched = await fetchCompileResultErrors(gateJobId)
    if (_fetched.count !== null) {
      beforeErrors = _fetched.count
      beforeErrorList = _fetched.errors.map(e => e.message || e.msg || '')
    }

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] resolveAll beforeErrors', { gateJobId, beforeErrors })

    const fileGroups    = groupIssuesByFile(issues)
    const fileGroupList = [...fileGroups.entries()]  // [[filename, issues], ...]

    setResolveState({
      ...INIT, open: true, mode: 'batch', step: 'fetching',
      batchTotal: fileGroupList.length,  // count = number of FILES, not errors
      batchDone: 0, batchLog: [],
      currentIssue: issues[0], beforeErrors,
    })

    const log = []
    const originalContents = {}           // filename → original content, for rollback
    const savedFiles    = new Map()       // filename → representative issue (for rollback saveFile call)

    // Build project manifest — list of all unique filenames for cross-file context
    const projectFiles = fileGroupList.map(([f]) => f.replace('.java', ''))

    // ── Process one FILE at a time — ONE AI call per file ────────────────────
    for (const [filename, fileIssues] of fileGroupList) {
      if (abortRef.current) break

      // Log entry for this file group
      const fileLogEntry = {
        issue: fileIssues[0],
        filename,
        errorCount: fileIssues.length,
        status: 'processing',
        message: `Fixing ${fileIssues.length} error${fileIssues.length > 1 ? 's' : ''}...`,
      }

      setResolveState(s => ({
        ...s, currentIssue: fileIssues[0], step: 'fetching',
        batchLog: [...log, fileLogEntry],
      }))

      // Step 1: Fetch full file content ONCE
      let fileContent
      try {
        fileContent = await fetchFileContent(fileIssues[0])
        originalContents[filename] = fileContent
      } catch (err) {
        fileLogEntry.status = 'error'
        fileLogEntry.message = `Fetch failed: ${err.message}`
        log.push(fileLogEntry)
        setResolveState(s => ({
          ...s, batchDone: s.batchDone + 1, batchLog: [...log],
        }))
        continue
      }

      // Step 2: Build ONE prompt with error snippets (±10 lines each, not full file)
      setResolveState(s => ({
        ...s, step: 'analyzing',
        batchLog: [...log, { ...fileLogEntry, message: `Analyzing ${fileIssues.length} errors with AI...` }],
      }))

      try {
        const prompt = buildBatchFilePrompt(fileContent, fileIssues, projectFiles)
        // Pass '' as code — snippets (±10 lines per error) are already embedded in the
        // prompt. Sending the full file would append 10k+ lines AFTER the patch
        // instructions, causing the AI to ignore the PATCH format entirely.
        const result = await callAI('', prompt, fileIssues[0])

        // Step 3: Parse line-level patches and apply to original file
        setResolveState(s => ({
          ...s, step: 'applying',
          batchLog: [...log, { ...fileLogEntry, message: 'Applying patches...' }],
        }))

        // Try patch-based response first, fall back to full-file response
        const patchResult = applyAIPatches(result, fileContent, fileIssues[0])
        let patched
        if (patchResult) {
          patched = patchResult.patchedContent
          if (import.meta.env.DEV)
            console.log(` [useAIResolve] Applied ${patchResult.patchCount} line-level patches to ${filename}`)
        } else {
          // Fallback: AI may have returned whole file in a code fence
          const totalLines = fileContent.split('\n').length
          const fallbackPatch = extractFileStructureCode(result, fileIssues[0], totalLines)
          if (!fallbackPatch) throw new Error('AI returned no patches — could not parse response.')
          patched = applyPatch(fileContent, fallbackPatch)
        }

        // Safety gate: brace balance + package declaration check
        const validation = validatePatchContent(patched, fileContent)
        if (!validation.valid) {
          throw new Error(`Patch rejected (${validation.reason})`)
        }

        // Step 4: Save the corrected file as overlay
        setResolveState(s => ({
          ...s, step: 'saving',
          batchLog: [...log, { ...fileLogEntry, message: 'Saving corrected file...' }],
        }))
        await saveFile(fileIssues[0], patched)
        savedFiles.set(filename, fileIssues[0])

        fileLogEntry.status = 'ok'
        fileLogEntry.message = `Fixed ${fileIssues.length} error${fileIssues.length > 1 ? 's' : ''}`
        log.push(fileLogEntry)

        if (import.meta.env.DEV)
          console.log(` [useAIResolve] resolveAll:  ${filename} — ${fileIssues.length} errors fixed in ONE call`)

      } catch (err) {
        fileLogEntry.status = 'error'
        fileLogEntry.message = `${err.message} — needs manual fix`
        log.push(fileLogEntry)
        if (import.meta.env.DEV)
          console.warn(` [useAIResolve] resolveAll:  ${filename} — ${err.message}`)
      }

      setResolveState(s => ({ ...s, batchDone: s.batchDone + 1, batchLog: [...log] }))
    }

    // ── Tally ─────────────────────────────────────────────────────────────────
    const appliedCount = log.filter(e => e.status === 'ok').length
    const failedCount  = log.filter(e => e.status === 'error').length
    const totalErrorsFixed = log.filter(e => e.status === 'ok').reduce((sum, e) => sum + (e.errorCount || 0), 0)
    const summary = {
      applied: appliedCount,
      failed: failedCount,
      total: fileGroupList.length,
      totalErrorsFixed,
      totalErrorCount: issues.length,
    }

    if (import.meta.env.DEV)
      console.log(' [useAIResolve] resolveAll patch phase done', { ...summary, savedFiles: [...savedFiles.keys()] })

    // If nothing was saved, abort without rerun
    if (savedFiles.size === 0) {
      setResolveState(s => ({
        ...s, step: 'error', batchLog: [...log], summary, beforeErrors,
        errorMessage: `All ${failedCount} file(s) failed — compile rerun skipped. Use Fix & Continue for manual repair.`,
      }))
      return
    }

    // ── Trigger single compile rerun ──────────────────────────────────────────
    setResolveState(s => ({ ...s, step: 'rerunning' }))
    try {
      const newJobId = await triggerRerun()
      if (import.meta.env.DEV)
        console.log(' [useAIResolve] resolveAll rerun triggered', { gateType, newJobId })

      const { finalJobId, afterErrors, afterErrorList } = await pollRerunWithErrors(newJobId)
      if (import.meta.env.DEV)
        console.log(' [useAIResolve] resolveAll rerun complete', { finalJobId, beforeErrors, afterErrors })

      // ── Regression guard with parse-unblock awareness ──────────────────────
      // When fixing parse-blocking errors (like ') expected'), javac can now
      // parse past the fix site and may discover deeper errors that were always
      // there. This is expected compiler behavior — NOT a regression.
      //
      // Only rollback if:
      //   1. Error count increased AND
      //   2. The increase is NOT explained by parse-unblocking
      let isRegression = false
      if (afterErrors !== null && beforeErrors !== null && afterErrors > beforeErrors) {
        const beforeHadParseBlockers = beforeErrorList.some(msg => isParseBlocking(msg))
        const afterHasParseBlockers  = (afterErrorList || []).some(e => isParseBlocking(e.message || e.msg || ''))

        if (beforeHadParseBlockers && !afterHasParseBlockers) {
          // Parse-blockers fixed, only semantic errors remain — this is progress
          if (import.meta.env.DEV)
            console.log(` [useAIResolve] Parse-unblock: ${beforeErrors}→${afterErrors} errors. ` +
              `Before had parse-blockers, after has none — keeping patches (this is progress, not regression).`)
          isRegression = false
        } else if (beforeHadParseBlockers && afterHasParseBlockers) {
          // Still has parse-blockers but count went up — new parse errors introduced
          if (import.meta.env.DEV)
            console.warn(` [useAIResolve] REGRESSION: ${beforeErrors}→${afterErrors} errors. ` +
              `After still has parse-blockers — rolling back.`)
          isRegression = true
        } else {
          // No parse-blockers before, but errors increased — true regression
          if (import.meta.env.DEV)
            console.warn(` [useAIResolve] REGRESSION: ${beforeErrors}→${afterErrors} errors. ` +
              `No parse-blockers in before set — rolling back.`)
          isRegression = true
        }
      }

      if (isRegression) {
        // Restore original content for each patched file
        await Promise.allSettled(
          [...savedFiles.entries()].map(([filename, repIssue]) => {
            const orig = originalContents[filename]
            return orig ? saveFile(repIssue, orig) : Promise.resolve()
          })
        )

        const rolledBack = [...savedFiles.keys()]
        setResolveState(s => ({
          ...s, step: 'done', newJobId: finalJobId, batchLog: [...log], summary,
          beforeErrors, afterErrors, rolledBack,
        }))
        // Do NOT call onRerunComplete — keep GateReportView on the original job
      } else {
        setResolveState(s => ({
          ...s, step: 'done', newJobId: finalJobId, batchLog: [...log], summary,
          beforeErrors, afterErrors,
        }))
        onRerunComplete?.(finalJobId)
      }
    } catch (err) {
      setResolveState(s => ({
        ...s, step: 'error', batchLog: [...log], summary, beforeErrors,
        errorMessage: `Re-run failed: ${err.message}`,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisContext, apiBase, gateType, onRerunComplete])

  // ── closeModal (exported) ─────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    abortRef.current = true
    setResolveState(INIT)
  }, [])

  return { resolveState, resolveIssue, resolveAll, closeModal }
}
